import { useMemo, useState, type JSX } from 'react';
import { usePaintings } from '../hooks/usePaintings';
import StyleFilters, { STYLE_FILTERS, type StyleFilter } from '../components/StyleFilters';
import MasonryGrid from '../components/MasonryGrid';
import Lightbox from '../components/Lightbox';
import type { Painting } from '../types/painting';

// Paintings come back from Firestore in creation order, which — since real
// paintings were bulk-imported alphabetically by filename — reads as an
// arbitrary alphabetical jumble rather than anything meaningful, especially
// scrolling through the single mobile column. Grouping by each painting's
// first style, in the same order they appear in STYLE_FILTERS, reads as a
// deliberate arrangement instead.
//
// Some adjacent styles are combined into one group rather than each getting
// its own: Pattachitra and Madhubani are both small, closely-related
// Indian folk-art groups, and giving each its own separately-laid-out block
// left an awkward, mostly-empty one. This only affects how the "All" view's
// theme groups are formed — the filter pills above stay per-style as
// before.
const THEME_GROUPS: readonly (readonly StyleFilter[])[] = [['Pattachitra', 'Madhubani']];

function themeGroupIndex(painting: Painting): number {
  const primaryStyle = painting.style[0] as StyleFilter;
  const explicitGroupIndex = THEME_GROUPS.findIndex((group) => group.includes(primaryStyle));
  if (explicitGroupIndex !== -1) return explicitGroupIndex;

  // Everything else gets its own bucket, ordered by STYLE_FILTERS and
  // placed after the explicit groups above.
  const styleIndex = STYLE_FILTERS.indexOf(primaryStyle);
  return THEME_GROUPS.length + (styleIndex === -1 ? STYLE_FILTERS.length : styleIndex);
}

// Splits an already themeGroupIndex-sorted list into contiguous runs
// sharing the same bucket, so MasonryGrid can balance each theme's block of
// cards independently instead of one continuous, ever-drifting column pack.
function groupByTheme(paintings: Painting[]): Painting[][] {
  const groups: Painting[][] = [];
  let lastGroupIndex: number | null = null;
  let currentGroup: Painting[] = [];
  for (const painting of paintings) {
    const groupIndex = themeGroupIndex(painting);
    if (groupIndex !== lastGroupIndex) {
      currentGroup = [];
      groups.push(currentGroup);
      lastGroupIndex = groupIndex;
    }
    currentGroup.push(painting);
  }
  return groups;
}

export default function Gallery(): JSX.Element {
  const { paintings, loading, error } = usePaintings();
  const [activeFilter, setActiveFilter] = useState<StyleFilter>(STYLE_FILTERS[0]);
  const [selected, setSelected] = useState<Painting | null>(null);

  const filteredPaintings = useMemo(() => {
    const filtered = activeFilter === 'All' ? paintings : paintings.filter((p) => p.style.includes(activeFilter));
    // Array.prototype.sort is stable, so paintings sharing a theme group
    // keep their original (creation-order) relative sequence within it.
    return [...filtered].sort((a, b) => themeGroupIndex(a) - themeGroupIndex(b));
  }, [paintings, activeFilter]);

  // Only the "All" view has distinct themes to keep separate — a
  // single-style filter is already one cohesive group.
  const groupedPaintings = useMemo(
    () => (activeFilter === 'All' ? groupByTheme(filteredPaintings) : [filteredPaintings]),
    [filteredPaintings, activeFilter],
  );

  if (loading) {
    return (
      <main className="mx-auto max-w-6xl px-4 py-12 text-center text-neutral-500">
        <p>Loading paintings…</p>
      </main>
    );
  }

  if (error !== null) {
    return (
      <main className="mx-auto max-w-6xl px-4 py-12 text-center text-red-600">
        <p>Couldn&apos;t load paintings: {error.message}</p>
      </main>
    );
  }

  return (
    <main className="mx-auto max-w-6xl px-4 pb-12">
      <StyleFilters activeFilter={activeFilter} onFilter={setActiveFilter} />

      {filteredPaintings.length === 0 ? (
        <p className="px-4 py-12 text-center text-neutral-500">No paintings in this style yet.</p>
      ) : (
        <div key={activeFilter}>
          <MasonryGrid groupedPaintings={groupedPaintings} onSelect={setSelected} />
        </div>
      )}

      {selected !== null ? (
        <Lightbox
          painting={selected}
          paintings={filteredPaintings}
          onClose={() => {
            setSelected(null);
          }}
        />
      ) : null}
    </main>
  );
}
